import { api } from './client'
import type { ApiEnvelope } from '@/types/api'
import type { AxiosError } from 'axios'

/**
 * Một khung giờ làm việc của robot tại một vị trí.
 * `id` chỉ có khi bản ghi đã tồn tại trong DB (dùng để phân loại update/delete khi lưu).
 */
export interface RobotSchedule {
    id?: string
    name?: string
    startTime: string   // dạng "HH:mm" khi đọc về
    endTime: string     // dạng "HH:mm" khi đọc về
    locationId: string
    workingDays?: string // ví dụ "2,3,4,5,6"
    status?: number
}

/** Item như backend trả về từ GET search-by-location */
interface RobotScheduleApiItem {
    id?: string
    name?: string
    startTime?: string
    endTime?: string
    workingDays?: string
    status?: number
    location?: { id?: string; name?: string } | null
    locationId?: string
}

function mapScheduleFromApiItem(
    item: RobotScheduleApiItem | undefined,
    fallback: { id?: string; locationId: string; startTime?: string; endTime?: string; status?: number; name?: string; workingDays?: string }
): RobotSchedule {
    return {
        id: item?.id ? String(item.id) : fallback.id,
        name: item?.name ?? fallback.name ?? '',
        startTime: extractHHmm(item?.startTime ?? fallback.startTime),
        endTime: extractHHmm(item?.endTime ?? fallback.endTime),
        locationId: item?.location?.id || item?.locationId || fallback.locationId,
        workingDays: item?.workingDays ?? fallback.workingDays ?? '',
        status: typeof item?.status === 'number' ? item.status : fallback.status ?? 1,
    }
}

function unwrapSuccessEnvelopeFromAxiosError<T>(error: unknown): ApiEnvelope<T> | null {
    const axiosError = error as AxiosError<ApiEnvelope<T>>
    const data = axiosError.response?.data
    if (data?.responseCode === 'OK') {
        return data
    }
    return null
}

/** Payload cho PUT .../update/{robotId}/location (dạng diff tường minh) */
export interface UpdateScheduleByLocationPayload {
    newScheduleList: Array<Omit<RobotSchedule, 'id' | 'status'>>
    updateScheduleList: Array<RobotSchedule & { id: string; status: number }>
    deleteScheduleIdList: string[]
}

/**
 * Trích "HH:mm" từ giá trị giờ của backend.
 * Backend có thể trả ISO đầy đủ ("2026-05-25T14:06:00.000Z") hoặc "HH:mm" thuần.
 * Ta chỉ quan tâm phần giờ-phút, KHÔNG để Date/UTC xen vào (tránh dịch timezone).
 */
function extractHHmm(value: string | undefined): string {
    if (!value) return ''
    // ISO: lấy phần ngay sau 'T'
    const tIndex = value.indexOf('T')
    if (tIndex !== -1) {
        return value.slice(tIndex + 1, tIndex + 6) // "HH:mm"
    }
    // đã là "HH:mm" hoặc "HH:mm:ss" -> cắt 5 ký tự đầu
    return value.slice(0, 5)
}

function unwrapList(responseData: unknown): RobotScheduleApiItem[] {
    // Backend trả 2 shape: mảng phẳng khi có data, hoặc Spring Page ({ content: [] }) khi rỗng.
    if (Array.isArray(responseData)) return responseData as RobotScheduleApiItem[]
    if (responseData && typeof responseData === 'object') {
        const content = (responseData as { content?: unknown }).content
        if (Array.isArray(content)) return content as RobotScheduleApiItem[]
    }
    return []
}

/**
 * Lấy chi tiết lịch trình của robot theo vị trí cụ thể.
 * Chuẩn hóa về mảng RobotSchedule để UI map thẳng vào TimePicker.
 */
export async function fetchRobotScheduleByLocation(
    robotId: string,
    locationId: string,
    signal?: AbortSignal
): Promise<RobotSchedule[]> {
    const response = await api.get<ApiEnvelope<unknown>>(
        `/v1/robot/schedule/search/${robotId}/${locationId}`,
        { signal }
    )
    return unwrapList(response.data.responseData).map((item) => ({
        id: String(item.id),
        name: item.name ?? '',
        startTime: extractHHmm(item.startTime),
        endTime: extractHHmm(item.endTime),
        locationId: item.location?.id ?? item.locationId ?? locationId,
        workingDays: item.workingDays ?? '',
        status: typeof item.status === 'number' ? item.status : undefined,
    }))
}

/**
 * Cập nhật toàn bộ khung giờ của robot theo vị trí trong một request (diff: thêm/sửa/xóa).
 */
export async function updateScheduleByLocation(
    robotId: string,
    payload: UpdateScheduleByLocationPayload,
): Promise<unknown> {
    try {
        const response = await api.post(
            `/v1/robot/schedule/update/${robotId}/location`,
            payload,
        )
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message
        throw new Error(message);
    }
}

/**
 * Tạo mới một lịch trình cho robot tại vị trí cụ thể.
 * POST /api/v1/robot/schedule/create/{robotId}
 */
export async function createSchedule(
    robotId: string,
    payload: {
        name?: string
        startTime: string
        endTime: string
        locationId: string
        workingDays?: string
    }
): Promise<RobotSchedule> {
    try {
        const response = await api.post<ApiEnvelope<RobotScheduleApiItem>>(
            `/v1/robot/schedule/create/${robotId}`,
            payload,
        )
        return mapScheduleFromApiItem(response.data.responseData, {
            locationId: payload.locationId,
            startTime: payload.startTime,
            endTime: payload.endTime,
            name: payload.name,
            workingDays: payload.workingDays,
            status: 1,
        })
    } catch (error: any) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<RobotScheduleApiItem>(error)
        if (okEnvelope) {
            return mapScheduleFromApiItem(okEnvelope.responseData, {
                locationId: payload.locationId,
                startTime: payload.startTime,
                endTime: payload.endTime,
                name: payload.name,
                workingDays: payload.workingDays,
                status: 1,
            })
        }
        const message = error?.response?.data?.responseMessage || error?.message
        throw new Error(message);
    }
}

/**
 * Cập nhật một lịch trình cụ thể.
 * PUT /api/v1/robot/schedule/update/{robotId}
 */
export async function updateSchedule(
    robotId: string,
    payload: {
        id: string
        name?: string
        startTime: string
        endTime: string
        locationId: string
        workingDays?: string
        status?: number
    }
): Promise<RobotSchedule> {
    try {
        const response = await api.put<ApiEnvelope<RobotScheduleApiItem>>(
            `/v1/robot/schedule/update/${robotId}`,
            payload,
        )
        return mapScheduleFromApiItem(response.data.responseData, {
            id: payload.id,
            locationId: payload.locationId,
            startTime: payload.startTime,
            endTime: payload.endTime,
            name: payload.name,
            workingDays: payload.workingDays,
            status: payload.status,
        })
    } catch (error: any) {
        const message = error?.response.data?.responseMessage || 'Đã xảy ra lỗi khi lưu lịch trình. Vui lòng thử lại.';
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<RobotScheduleApiItem>(error)
        if (okEnvelope) {
            return mapScheduleFromApiItem(okEnvelope.responseData, {
                id: payload.id,
                locationId: payload.locationId,
                startTime: payload.startTime,
                endTime: payload.endTime,
                name: payload.name,
                workingDays: payload.workingDays,
                status: payload.status,
            })
        }
        throw new Error(message);
    }
}

/**
 * Xóa một lịch trình cụ thể.
 * DELETE /api/v1/robot/schedule/update/{robotId}/{scheduleId}
 */
export async function deleteSchedule(
    robotId: string,
    scheduleId: string,
): Promise<unknown> {
    try {
        const response = await api.delete(
            `/v1/robot/schedule/update/${robotId}/${scheduleId}`,
        )
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message
        throw new Error(message);
    }
}